import { Platform } from 'react-native';
import RNBlobUtil from 'react-native-blob-util';
import * as RNFS from 'react-native-fs';

/**
 * Put a file that is ALREADY ON DISK into the Android public Downloads
 * folder (#1069 Story 7.5, D11, AC 21).
 *
 * The path-taking core, extracted so that bytes which never entered
 * JavaScript (a zip streamed to `CacheDir` by `react-native-blob-util`) and
 * bytes which did (`saveToAndroidDownloads`' base64) share one MediaStore
 * hand-off rather than two copies that drift.
 *
 * - API 29+ (Android 10+): copies the file into the MediaStore Downloads
 *   collection via `copyToMediaStore`. The temp file is cleaned up on a
 *   best-effort basis, whether or not the copy succeeded.
 * - API < 29 (Android 9 and below): moves the file into the shared Downloads
 *   directory with `RNFS.moveFile`, which leaves nothing behind to unlink
 *   when it succeeds; when it throws, the temp file is unlinked first.
 */
export async function copyFileToAndroidDownloads(
  tempPath: string,
  fileName: string,
  mimeType: string,
): Promise<void> {
  const androidApiLevel = Platform.Version as number;

  if (androidApiLevel >= 29) {
    try {
      await RNBlobUtil.MediaCollection.copyToMediaStore(
        { name: fileName, parentFolder: '', mimeType },
        'Download',
        tempPath,
      );
    } finally {
      RNBlobUtil.fs.unlink(tempPath).catch((e: unknown) => {
        console.warn('Failed to clean up temp file after MediaStore copy:', e);
      });
    }
    return;
  }

  const destPath = `${RNFS.DownloadDirectoryPath}/${fileName}`;
  try {
    await RNFS.moveFile(tempPath, destPath);
  } catch (e: unknown) {
    // A failed move leaves the source where it was (review P9): the temp
    // file is this function's to clean up on both branches, not only the
    // MediaStore one.
    await RNBlobUtil.fs.unlink(tempPath).catch((unlinkError: unknown) => {
      console.warn('Failed to clean up temp file after move:', unlinkError);
    });
    throw e;
  }
}

/**
 * Save a base64-encoded file to the Android public Downloads folder.
 *
 * - API 29+ (Android 10+): writes to a temp cache file, then hands the path to
 *   `copyFileToAndroidDownloads` (MediaStore copy, temp file cleaned up).
 * - API < 29 (Android 9 and below): writes directly to the shared Downloads
 *   directory using `RNFS.writeFile`. ⚠️ Deliberately NOT through the temp
 *   path: the existing suite pins that no cache write happens below 29, and
 *   a direct write is one operation where write-then-move is two.
 */
export async function saveToAndroidDownloads(
  base64Data: string,
  fileName: string,
  mimeType: string,
): Promise<void> {
  const androidApiLevel = Platform.Version as number;

  if (androidApiLevel >= 29) {
    const tempPath = `${RNBlobUtil.fs.dirs.CacheDir}/${fileName}`;
    try {
      await RNBlobUtil.fs.writeFile(tempPath, base64Data, 'base64');
    } catch (e: unknown) {
      // A failed write (disk full) can leave a PARTIAL file, and nothing
      // below owns it yet: `copyFileToAndroidDownloads` cleans up only what
      // it was handed. Unlink it here, then rethrow the WRITE error
      // (#1069 MR !597 review).
      await RNBlobUtil.fs.unlink(tempPath).catch((unlinkError: unknown) => {
        console.warn('Failed to clean up temp file after write:', unlinkError);
      });
      throw e;
    }
    await copyFileToAndroidDownloads(tempPath, fileName, mimeType);
  } else {
    const destPath = `${RNFS.DownloadDirectoryPath}/${fileName}`;
    await RNFS.writeFile(destPath, base64Data, 'base64');
  }
}
